import { useEffect, useCallback } from 'react';
import { logger } from '@/lib/logger';

const SESSION_ACTIVE_KEY = 'kc_session_active';
const SESSION_CLOSING_KEY = 'kc_session_closing';
const REFRESH_THRESHOLD_MS = 3000; // 3 seconds - if page reloads within this time, it's a refresh

/**
 * Hook to manage session lifecycle and distinguish between tab close vs page refresh
 * - Page refresh: Preserves session (detected via sessionStorage)
 * - Tab close: Session cleanup handled by 30-min inactivity timeout
 *
 * Note: We intentionally do NOT terminate session on pagehide/visibilitychange
 * because these events fire on tab switch too, not just tab close.
 * The 30-minute inactivity timeout serves as the safety net for closed tabs.
 *
 * Uses sessionStorage (tab-scoped) to detect refresh vs close
 */
export function useSessionLifecycle({ isAuthenticated }) {
  // Check if this is a page refresh (not a fresh tab)
  const isPageRefresh = useCallback(() => {
    const closingTimestamp = sessionStorage.getItem(SESSION_CLOSING_KEY);

    if (closingTimestamp) {
      const timeSinceClosing = Date.now() - parseInt(closingTimestamp, 10);
      // If we're loading within threshold of the "closing" timestamp, it's a refresh
      return timeSinceClosing < REFRESH_THRESHOLD_MS;
    }

    // Check if session was active before (another way to detect refresh)
    const wasActive = sessionStorage.getItem(SESSION_ACTIVE_KEY);
    return wasActive === 'true';
  }, []);

  // Handle beforeunload - mark potential closing for refresh detection
  const handleBeforeUnload = useCallback(() => {
    // Mark that we're potentially closing (used to detect refresh vs close)
    sessionStorage.setItem(SESSION_CLOSING_KEY, Date.now().toString());
    logger.debug('beforeunload: marked session as potentially closing');
  }, []);

  // Initialize session tracking
  useEffect(() => {
    if (!isAuthenticated) {
      // Clean up session markers when not authenticated
      sessionStorage.removeItem(SESSION_ACTIVE_KEY);
      sessionStorage.removeItem(SESSION_CLOSING_KEY);
      return;
    }

    // Check if this is a refresh
    const isRefresh = isPageRefresh();

    if (isRefresh) {
      logger.debug('Detected page refresh - preserving session');
      // Clear the closing marker since we're back
      sessionStorage.removeItem(SESSION_CLOSING_KEY);
    } else {
      logger.debug('Fresh tab/session detected');
    }

    // Mark session as active
    sessionStorage.setItem(SESSION_ACTIVE_KEY, 'true');

    // Set up beforeunload listener for refresh detection
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isAuthenticated, isPageRefresh, handleBeforeUnload]);

  return {
    isPageRefresh: isPageRefresh(),
  };
}
